import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Download, Search } from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { checkPermission } from "../../../Configurations/PermissionConfigrations";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";

const PAGE_SIZE = 10;

function defaultFrom() {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return toInputDate(d);
}

function defaultTo() {
  return toInputDate(new Date());
}

function toInputDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return {
    date: d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

function fmtInr(val) {
  const x = Number(val);
  if (!Number.isFinite(x)) return "₹ 0";
  const formatted = Math.round(x).toLocaleString("en-IN");
  return `₹ ${formatted}`;
}

function emptyFilters() {
  return {
    from: defaultFrom(),
    to: defaultTo(),
    offer_status: "",
    training_program_id: "",
    recipient_type: "",
    audience_type: "",
    redemption_status: "",
    search: "",
  };
}

function offerStatusClass(status) {
  const key = String(status || "").toUpperCase();
  if (key === "ACTIVE") return "ems-or-pill ems-or-pill-active";
  if (key === "EXPIRED") return "ems-or-pill ems-or-pill-expired";
  return "ems-or-pill ems-or-pill-paused";
}

function redemptionStatusClass(status) {
  const key = String(status || "").toUpperCase();
  if (key === "REDEEMED") return "ems-or-pill ems-or-pill-redeemed";
  if (key === "APPLIED") return "ems-or-pill ems-or-pill-applied";
  if (key === "CLAIMED") return "ems-or-pill ems-or-pill-claimed";
  if (key === "CANCELLED") return "ems-or-pill ems-or-pill-cancelled";
  if (key === "EXPIRED") return "ems-or-pill ems-or-pill-expired";
  return "ems-or-pill";
}

function ReportCards({ empty, children }) {
  return (
    <div className="ems-or-cards">
      {children || <div className="ems-or-card-empty">{empty}</div>}
    </div>
  );
}

function ReportCard({ title, subtitle, fields }) {
  return (
    <article className="ems-or-card">
      <div className="ems-or-card-title">{title || "—"}</div>
      {subtitle ? <div className="ems-or-card-sub">{subtitle}</div> : null}
      <div className="ems-or-card-rows">
        {fields.map((field) => (
          <div className="ems-or-card-row" key={field.label}>
            <div className="ems-or-card-label">{field.label}</div>
            <div className="ems-or-card-value">{field.value}</div>
          </div>
        ))}
      </div>
    </article>
  );
}

export default function OfferRedemptionsPage() {
  const organizationId = useAuthStore(
    (s) => s.userData?.organization?.organization_id
  );
  const organizationName = useAuthStore(
    (s) => s.userData?.organization?.organization_name
  );
  const permissionList = usePermissionDataStore((s) => s.Permission);
  const canDownloadReport = checkPermission(
    permissionList,
    "DOWNLOAD_OFFER_REDEMPTION_REPORT"
  );

  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [offers, setOffers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/ems/offer-redemptions/report`,
        {
          headers: getApiHeaders(),
          params: {
            organization_id: organizationId,
            from: applied.from || undefined,
            to: applied.to || undefined,
            offer_status: applied.offer_status || undefined,
            training_program_id: applied.training_program_id || undefined,
            recipient_type: applied.recipient_type || undefined,
            audience_type: applied.audience_type || undefined,
            redemption_status: applied.redemption_status || undefined,
            search: applied.search || undefined,
          },
        }
      );
      const payload = res.data?.data ?? {};
      setOffers(Array.isArray(payload.offers) ? payload.offers : []);
      setRedemptions(
        Array.isArray(payload.redemptions) ? payload.redemptions : []
      );
      setPrograms(Array.isArray(payload.programs) ? payload.programs : []);
      setPage(1);
    } catch (err) {
      setOffers([]);
      setRedemptions([]);
      const message = String(err?.response?.data?.message || "");
      if (
        err?.response?.status === 404 ||
        /offer not found/i.test(message)
      ) {
        return;
      }
      toast.error(message || "Could not load redemptions report.");
    } finally {
      setLoading(false);
    }
  }, [organizationId, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(redemptions.length / PAGE_SIZE));
  const pagedRedemptions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return redemptions.slice(start, start + PAGE_SIZE);
  }, [redemptions, page]);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const downloadPdf = async () => {
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
      const marginX = 32;
      let y = 40;

      doc.setFontSize(14);
      doc.text("Offers & Redemptions Report", marginX, y);
      y += 16;
      doc.setFontSize(9);
      doc.setTextColor(90);
      doc.text(
        `${organizationName || ""}  ·  ${fmtDate(applied.from)} to ${fmtDate(applied.to)}`,
        marginX,
        y
      );
      doc.setTextColor(0);
      y += 22;

      doc.setFont(undefined, "bold");
      doc.text("Offers List", marginX, y);
      doc.setFont(undefined, "normal");
      y += 14;

      const offerCols = [marginX, 90, 200, 300, 350, 430, 500, 560, 640, 700];
      const offerHeads = [
        "Code",
        "Offer",
        "Program",
        "Discount",
        "Valid From",
        "Valid Until",
        "Status",
        "Recipient",
        "Redeemed",
        "Final",
      ];
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      offerHeads.forEach((h, i) => doc.text(h, offerCols[i], y));
      doc.setFont(undefined, "normal");
      y += 10;
      doc.setDrawColor(200);
      doc.line(marginX, y, 810, y);
      y += 12;

      offers.forEach((row) => {
        if (y > 540) {
          doc.addPage();
          y = 40;
        }
        const cells = [
          row.offer_code || "—",
          row.offer_name || "—",
          row.program_name || "—",
          row.discount_label || "—",
          fmtDate(row.valid_from),
          fmtDate(row.valid_until),
          row.offer_status_label || row.offer_status || "—",
          row.recipient_label || "—",
          String(row.redemptions_count ?? 0),
          fmtInr(row.final_amount),
        ];
        cells.forEach((c, i) =>
          doc.text(String(c).slice(0, 22), offerCols[i], y)
        );
        y += 13;
      });

      y += 18;
      if (y > 500) {
        doc.addPage();
        y = 40;
      }
      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.text("Redemption Details", marginX, y);
      doc.setFont(undefined, "normal");
      y += 14;
      doc.setFontSize(8);
      const redCols = [marginX, 130, 200, 320, 470, 540, 620, 700];
      const redHeads = [
        "Date & Time",
        "Code",
        "Offer",
        "Recipient",
        "Type",
        "Original",
        "Discount",
        "Status",
      ];
      doc.setFont(undefined, "bold");
      redHeads.forEach((h, i) => doc.text(h, redCols[i], y));
      doc.setFont(undefined, "normal");
      y += 10;
      doc.line(marginX, y, 810, y);
      y += 12;

      redemptions.forEach((row) => {
        if (y > 540) {
          doc.addPage();
          y = 40;
        }
        const dt = fmtDateTime(row.occurred_at);
        const cells = [
          `${dt.date} ${dt.time}`,
          row.offer_code || "—",
          row.offer_name || "—",
          row.person_name || "—",
          row.recipient_label || "—",
          fmtInr(row.original_amount),
          fmtInr(row.discount_amount),
          row.redemption_status_label || row.redemption_status || "—",
        ];
        cells.forEach((c, i) =>
          doc.text(String(c).slice(0, 24), redCols[i], y)
        );
        y += 13;
      });

      doc.save("offers-redemptions-report.pdf");
    } catch (err) {
      toast.error("Could not generate PDF.");
    }
  };

  return (
    <div className="ems-dashboard ems-fade-up ems-or-report">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Offers & Redemptions Report</h1>
        </div>
        <div className="ems-or-head-right">
          <div className="ems-or-dates">
            <label className="ems-or-date-field">
              <span>FROM</span>
              <input
                type="date"
                className="ems-select"
                value={draft.from}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, from: e.target.value }))
                }
              />
            </label>
            <label className="ems-or-date-field">
              <span>TO</span>
              <input
                type="date"
                className="ems-select"
                value={draft.to}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, to: e.target.value }))
                }
              />
            </label>
          </div>
          {canDownloadReport && (
            <button
              type="button"
              className="ems-btn ems-primary ems-or-pdf-btn"
              onClick={downloadPdf}
              disabled={loading}
            >
              <Download size={15} />
              Download PDF
            </button>
          )}
        </div>
      </div>

      <div className="ems-card ems-or-filters">
        <label className="ems-or-filter">
          <span>Offer Status</span>
          <select
            className="ems-select"
            value={draft.offer_status}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, offer_status: e.target.value }))
            }
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </label>
        <label className="ems-or-filter">
          <span>Training Program</span>
          <select
            className="ems-select"
            value={draft.training_program_id}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                training_program_id: e.target.value,
              }))
            }
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option
                key={p.organization_ems_training_program_id}
                value={p.organization_ems_training_program_id}
              >
                {p.program_name}
              </option>
            ))}
          </select>
        </label>
        <label className="ems-or-filter">
          <span>Recipient Type</span>
          <select
            className="ems-select"
            value={draft.recipient_type}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, recipient_type: e.target.value }))
            }
          >
            <option value="">All Types</option>
            <option value="EXTERNAL">External</option>
            <option value="INTERNAL">Internal</option>
          </select>
        </label>
        <label className="ems-or-filter">
          <span>Audience Type</span>
          <select
            className="ems-select"
            value={draft.audience_type}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, audience_type: e.target.value }))
            }
          >
            <option value="">All Types</option>
            <option value="PUBLIC">Public</option>
            <option value="ALL_LEADS">All Leads</option>
            <option value="LEAD">Lead</option>
            <option value="ALL_STUDENTS">All Students</option>
            <option value="ALUMNI">Alumni</option>
            <option value="TRAINING_PROGRAM">Training Program</option>
            <option value="TRAINING_CATEGORY">Training Category</option>
            <option value="BATCH">Batch</option>
            <option value="STUDENT">Student</option>
          </select>
        </label>
        <label className="ems-or-filter">
          <span>Redemption Status</span>
          <select
            className="ems-select"
            value={draft.redemption_status}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                redemption_status: e.target.value,
              }))
            }
          >
            <option value="">All Statuses</option>
            <option value="REDEEMED">Redeemed</option>
            <option value="APPLIED">Applied</option>
            <option value="CLAIMED">Claimed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </label>
        <div className="ems-or-search">
          <Search size={15} />
          <input
            type="search"
            placeholder="Search by offer code, name or program..."
            value={draft.search}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, search: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") setApplied({ ...draft });
            }}
          />
        </div>
        <button
          type="button"
          className="ems-btn ems-ghost ems-or-apply"
          onClick={() => setApplied({ ...draft })}
          disabled={loading}
        >
          Apply Filters
        </button>
      </div>

      <div className="ems-card ems-or-table-card">
        <h2 className="ems-or-section-title">Offers List</h2>
        <div className="ems-or-table-wrap">
          <table className="ems-or-table">
            <thead>
              <tr>
                <th>Offer Code</th>
                <th>Offer Name</th>
                <th>Training Program</th>
                <th>Discount</th>
                <th>Valid From</th>
                <th>Valid Until</th>
                <th>Status</th>
                <th>Recipient Type</th>
                <th>Audience</th>
                <th>Redemptions</th>
                <th>Original Amount</th>
                <th>Discount Given</th>
                <th>Final Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="ems-or-empty">
                    Loading report…
                  </td>
                </tr>
              ) : offers.length === 0 ? (
                <tr>
                  <td colSpan={13} className="ems-or-empty">
                    No offers in this range.
                  </td>
                </tr>
              ) : (
                offers.map((row) => (
                  <tr key={row.organization_ems_offer_id}>
                    <td>{row.offer_code || "—"}</td>
                    <td>{row.offer_name || "—"}</td>
                    <td>{row.program_name || "—"}</td>
                    <td>{row.discount_label || "—"}</td>
                    <td>{fmtDate(row.valid_from)}</td>
                    <td>{fmtDate(row.valid_until)}</td>
                    <td>
                      <span className={offerStatusClass(row.offer_status)}>
                        {row.offer_status_label || row.offer_status}
                      </span>
                    </td>
                    <td>{row.recipient_label || "—"}</td>
                    <td>{row.audience_label || "—"}</td>
                    <td>{row.redemptions_count ?? 0}</td>
                    <td>{fmtInr(row.original_amount)}</td>
                    <td>{fmtInr(row.discount_amount)}</td>
                    <td>{fmtInr(row.final_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ReportCards empty={loading ? "Loading report…" : "No offers in this range."}>
          {!loading && offers.length > 0
            ? offers.map((row) => (
                <ReportCard
                  key={row.organization_ems_offer_id}
                  title={row.offer_name || "—"}
                  fields={[
                    { label: "Offer Code", value: row.offer_code || "—" },
                    { label: "Training Program", value: row.program_name || "—" },
                    { label: "Discount", value: row.discount_label || "—" },
                    { label: "Valid From", value: fmtDate(row.valid_from) },
                    { label: "Valid Until", value: fmtDate(row.valid_until) },
                    {
                      label: "Status",
                      value: (
                        <span className={offerStatusClass(row.offer_status)}>
                          {row.offer_status_label || row.offer_status}
                        </span>
                      ),
                    },
                    { label: "Recipient Type", value: row.recipient_label || "—" },
                    { label: "Audience", value: row.audience_label || "—" },
                    { label: "Redemptions", value: row.redemptions_count ?? 0 },
                    { label: "Original Amount", value: fmtInr(row.original_amount) },
                    { label: "Discount Given", value: fmtInr(row.discount_amount) },
                    { label: "Final Amount", value: fmtInr(row.final_amount) },
                  ]}
                />
              ))
            : null}
        </ReportCards>
      </div>

      <div className="ems-card ems-or-table-card">
        <h2 className="ems-or-section-title">Redemption Details</h2>
        <div className="ems-or-table-wrap">
          <table className="ems-or-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Offer Code</th>
                <th>Offer Name</th>
                <th>Recipient</th>
                <th>Recipient Type</th>
                <th>Original Amount</th>
                <th>Discount Amount</th>
                <th>Final Amount</th>
                <th>Redemption Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="ems-or-empty">
                    Loading report…
                  </td>
                </tr>
              ) : pagedRedemptions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ems-or-empty">
                    No redemptions in this range.
                  </td>
                </tr>
              ) : (
                pagedRedemptions.map((row) => {
                  const dt = fmtDateTime(row.occurred_at);
                  return (
                    <tr key={row.organization_ems_offer_redemption_id}>
                      <td>
                        <div>{dt.date}</div>
                        <div className="ems-or-time">{dt.time}</div>
                      </td>
                      <td>{row.offer_code || "—"}</td>
                      <td>{row.offer_name || "—"}</td>
                      <td>{row.person_name || "—"}</td>
                      <td>{row.recipient_label || "—"}</td>
                      <td>{fmtInr(row.original_amount)}</td>
                      <td>{fmtInr(row.discount_amount)}</td>
                      <td>{fmtInr(row.final_amount)}</td>
                      <td>
                        <span
                          className={redemptionStatusClass(
                            row.redemption_status
                          )}
                        >
                          {row.redemption_status_label ||
                            row.redemption_status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ReportCards
          empty={loading ? "Loading report…" : "No redemptions in this range."}
        >
          {!loading && pagedRedemptions.length > 0
            ? pagedRedemptions.map((row) => {
                const dt = fmtDateTime(row.occurred_at);
                const when =
                  dt === "—" ? "—" : `${dt.date}${dt.time ? ` · ${dt.time}` : ""}`;
                return (
                  <ReportCard
                    key={row.organization_ems_offer_redemption_id}
                    title={row.person_name || row.offer_name || "—"}
                    fields={[
                      { label: "Date & Time", value: when },
                      { label: "Offer Code", value: row.offer_code || "—" },
                      { label: "Offer Name", value: row.offer_name || "—" },
                      { label: "Recipient Type", value: row.recipient_label || "—" },
                      { label: "Original Amount", value: fmtInr(row.original_amount) },
                      { label: "Discount Amount", value: fmtInr(row.discount_amount) },
                      { label: "Final Amount", value: fmtInr(row.final_amount) },
                      {
                        label: "Status",
                        value: (
                          <span
                            className={redemptionStatusClass(row.redemption_status)}
                          >
                            {row.redemption_status_label || row.redemption_status}
                          </span>
                        ),
                      },
                    ]}
                  />
                );
              })
            : null}
        </ReportCards>
        <div className="ems-or-pager">
          <span>
            Showing {redemptions.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} –{" "}
            {Math.min(page * PAGE_SIZE, redemptions.length)} of{" "}
            {redemptions.length} records
          </span>
          <div className="ems-or-pages">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            {pageButtons.map((n) => (
              <button
                key={n}
                type="button"
                className={n === page ? "is-active" : ""}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
